// LQC-2254 - settlement date derivation for tickets built by hand. The app had no
// T+N, business-day or holiday logic before this, and still has no holiday calendar:
// these skip weekends only, so a settlement date falling on a market holiday is the
// user's to correct. Dates are yyyy-MM-dd strings end to end, matching every other
// outbound date in the app.

export const DEFAULT_SETTLEMENT_BUSINESS_DAYS = 2;

const SATURDAY = 6;
const SUNDAY = 0;

// Local-time formatting on purpose. toISOString() converts to UTC, which shifts the
// date by a day for anyone east of Greenwich - the whole point of a date-only string
// is that it does not move.
// LQC-2677 - a native <input type="date"> will happily yield a five or six digit year
// ("22029-01-03" is a legal HTML date), but .NET DateTime stops at 9999, so such a value
// fails deserialisation on every trade endpoint and the API reports the whole body as
// missing. Every outbound date is checked through here.
export const MIN_TICKET_DATE = "1900-01-01";
export const MAX_TICKET_DATE = "9999-12-31";

export function isValidISODateString(value: string | null | undefined): boolean {
  if (!value) return false;
  // Four digit year only - this is the check the date input cannot make for us.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  if (value < MIN_TICKET_DATE || value > MAX_TICKET_DATE) return false;
  // Round trip so 2026-02-31 and friends are rejected too.
  const parsed = new Date(`${value}T00:00:00Z`);
  if (isNaN(parsed.getTime())) return false;
  return parsed.toISOString().slice(0, 10) === value;
}

export function returnISODateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Parse yyyy-MM-dd as a LOCAL date. new Date("2026-08-12") parses as UTC midnight,
// which reads back as the previous day in negative-offset timezones.
export function returnDateFromISOString(isoDate: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate ?? "");
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return isNaN(date.getTime()) ? null : date;
}

export function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === SATURDAY || day === SUNDAY;
}

export function returnTodayISODate(): string {
  return returnISODateString(new Date());
}

// Add N business days, skipping weekends. A start date that is itself a weekend
// still counts forward from where it sits.
export function returnDateOffsetByBusinessDays(
  isoDate: string,
  businessDays: number,
): string {
  const start = returnDateFromISOString(isoDate);
  if (!start) return "";
  if (businessDays <= 0) return isoDate;

  const result = new Date(start.getTime());
  let added = 0;
  while (added < businessDays) {
    result.setDate(result.getDate() + 1);
    if (!isWeekend(result)) added += 1;
  }
  return returnISODateString(result);
}

// T+2 business days from the trade date, the default a hand-built bond ticket opens
// with. Conventions vary by market (USTs are T+1), so the user can always change it.
export function returnDefaultSettlementDate(tradeDateISO?: string): string {
  const tradeDate = tradeDateISO || returnTodayISODate();
  return returnDateOffsetByBusinessDays(
    tradeDate,
    DEFAULT_SETTLEMENT_BUSINESS_DAYS,
  );
}

// LQC-2995 - T+N from the security's SettlementDays. 0 is real (T+0); only a missing
// or invalid value falls back to the default.
export function returnSettlementDateForDays(
  tradeDateISO: string,
  settlementDays: number | null | undefined,
): string {
  const days =
    typeof settlementDays === "number" && settlementDays >= 0
      ? settlementDays
      : DEFAULT_SETTLEMENT_BUSINESS_DAYS;
  return returnDateOffsetByBusinessDays(tradeDateISO || returnTodayISODate(), days);
}

// Weekdays after fromISO up to and including toISO; 0 when toISO is not later.
export function returnBusinessDaysBetween(fromISO: string, toISO: string): number {
  const from = returnDateFromISOString(fromISO);
  const to = returnDateFromISOString(toISO);
  if (!from || !to || to <= from) return 0;
  const cursor = new Date(from.getTime());
  let count = 0;
  while (cursor < to) {
    cursor.setDate(cursor.getDate() + 1);
    if (!isWeekend(cursor)) count += 1;
  }
  return count;
}

export interface IDatedTicket {
  tradeDate: string;
  settlementDate: string;
  startDate?: string;
}

// LQC-2981 - a ticket opened before local midnight still holds yesterday. Roll the trade
// date to today, keep settlement's T+N, and move a start date that followed settlement.
export function returnRolledTradeDateChanges(
  ticket: IDatedTicket,
  todayISO: string,
): Partial<IDatedTicket> | null {
  if (!isValidISODateString(ticket.tradeDate) || ticket.tradeDate >= todayISO) return null;
  const changes: Partial<IDatedTicket> = { tradeDate: todayISO };
  if (!isValidISODateString(ticket.settlementDate)) return changes;

  const settlementDays = returnBusinessDaysBetween(ticket.tradeDate, ticket.settlementDate);
  changes.settlementDate = returnDateOffsetByBusinessDays(todayISO, settlementDays);
  if (ticket.startDate && ticket.startDate === ticket.settlementDate) {
    changes.startDate = changes.settlementDate;
  }
  return changes;
}

// Until the next local 00:00, for the midnight trade-date rollover (LQC-2981).
export function returnMsUntilNextLocalMidnight(now: Date = new Date()): number {
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return midnight.getTime() - now.getTime();
}
